import { FieldWrapper } from "components/ui/FieldWrapper";
import { Input, type InputProps } from "components/ui/Input";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

export type RhfInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control?: Control<T>;
  label?: string;
  required?: boolean;
  hint?: string;
} & Omit<InputProps, "tone">;

/**
 * React Hook Form アダプタ。JSX は ui/Input と ui/FieldWrapper に完全委譲する。
 * control 省略時は FormContext から自動取得（useController の標準挙動）。
 * @param {RhfInputProps<T>} props RhfInput の props
 * @return {JSX.Element} FieldWrapper で包まれた Input
 */
export function RhfInput<T extends FieldValues>({
  name,
  control,
  label,
  required,
  hint,
  ...inputProps
}: RhfInputProps<T>): JSX.Element {
  const { field, fieldState } = useController<T>({ name, control });

  return (
    <FieldWrapper
      label={label}
      required={required}
      hint={hint}
      error={fieldState.error?.message}
    >
      <Input
        {...inputProps}
        name={field.name}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        ref={field.ref}
        tone={fieldState.error ? "invalid" : "default"}
      />
    </FieldWrapper>
  );
}
